import { useParams, Link } from 'react-router-dom';
import { SEO } from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById, categories, comics } from '@/mocks/comics';
import { useEffect } from 'react';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '0', 10);
  const comic = getComicById(comicId);
  const categoryInfo = categories.find((c) => c.slug === comic?.category);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!comic) {
    return (
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">您要查找的漫画不存在或已被移除</p>
          <Link to="/" className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 4);

  return (
    <>
      <SEO
        title={`${comic.title}_${comic.author}_高清韩国漫画在线免费阅读-韩漫网`}
        description={`${comic.title}是由${comic.author}创作的${categoryInfo?.name || ''}类韩国漫画，${comic.description}韩漫网为您提供${comic.title}免费在线阅读，共${comic.chapters}话，评分${comic.rating}。`}
        keywords={`${comic.title},${comic.author},${categoryInfo?.name}漫画,高清韩国漫画,免费韩漫在线阅读,韩漫网`}
        canonicalUrl={`https://www.hanmanwang.com/article/${comic.id}`}
        ogImage={comic.coverUrl}
        schemaType="Article"
      />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类', href: '/category/all' },
              { label: categoryInfo?.name || '全部', href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Cover Image */}
            <div className="flex-shrink-0 w-full sm:w-64 lg:w-72 mx-auto lg:mx-0">
              <div className="relative rounded-lg overflow-hidden border border-background-200/70">
                <img
                  src={comic.coverUrl}
                  alt={`${comic.title} - ${comic.author}创作的${categoryInfo?.name}漫画`}
                  title={`${comic.title} - 高清韩国漫画`}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-primary-500 text-background-50 text-sm rounded-md font-bold">
                    {comic.rating}
                  </span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                {categoryInfo && (
                  <Link
                    to={`/category/${comic.category}`}
                    className="px-3 py-1 bg-secondary-100 text-secondary-600 text-sm rounded-full whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {categoryInfo.name}
                  </Link>
                )}
                <span className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                  comic.status === '已完结'
                    ? 'bg-accent-100 text-accent-600'
                    : 'bg-primary-100 text-primary-600'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <span className="text-xs text-foreground-500">作者</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.author}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">总话数</span>
                  <p className="text-sm font-medium text-foreground-900">共 {comic.chapters} 话</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">最近更新</span>
                  <p className="text-sm font-medium text-foreground-900">{comic.updateDate}</p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex items-center gap-3">
                <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm">
                  开始阅读
                </button>
                <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg border border-background-200/70 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  加入收藏
                </button>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <section className="mt-10">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
              <a id="chapters">章节列表</a>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {Array.from({ length: Math.min(comic.chapters, 50) }, (_, i) => (
                <button
                  key={i}
                  className="px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all text-left whitespace-nowrap cursor-pointer"
                >
                  第 {comic.chapters - i} 话
                </button>
              ))}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="mt-10 pt-10 border-t border-background-200/70">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop="">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/article/${c.id}`}
                    className="group block bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all overflow-hidden cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={c.coverUrl}
                        alt={c.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 truncate">{c.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-primary-500 font-medium">{c.rating}</span>
                        <span className="text-xs text-foreground-500">{c.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://www.hanmanwang.com/" },
              { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://www.hanmanwang.com/category/all" },
              { "@type": "ListItem", position: 3, name: categoryInfo?.name || '全部', item: `https://www.hanmanwang.com/category/${comic.category}` },
              { "@type": "ListItem", position: 4, name: comic.title },
            ],
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${comic.title} - 高清韩国漫画`,
            author: { "@type": "Person", name: comic.author },
            datePublished: "2026-01-01",
            dateModified: comic.updateDate,
            description: comic.description,
            image: comic.coverUrl,
            publisher: { "@type": "Organization", name: "韩漫网" },
          }),
        }}
      />
    </>
  );
}